// 获取状态标签
export const getStatusLabel = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    'pending_payment': '待付款',
    'cancelled': '已取消',
    'pending_print': '待打印',
    'pending_delivery': '待收货',
    'completed': '已完成',
    '_re_refunded': '已退款',
    '_re_after_sales': '售后中',
  };
  return statusMap[status] || status;
};

// 生成订单基础信息
const generateBasicInfo = (orderData: any): string => {
  let info = '【订单基础信息】\n';
  info += `订单编号：${orderData.id || '无'}\n`;
  info += `订单名称：${orderData.name || '无'}\n`;
  info += `订单状态：${getStatusLabel(orderData.status) || '无'}\n`;
  info += `创建时间：${orderData.createTime || '无'}\n`;
  info += `用户ID：${orderData.userId || '无'}\n\n`;
  return info;
};

// 生成收货地址信息
const generatePostInfo = (orderData: any): string => {
  let info = '【收货地址信息】\n';
  if (orderData.postinfo) {
    const { postinfo } = orderData;
    info += `收货人：${postinfo.name || '无'}\n`;
    info += `联系电话：${postinfo.phone || '无'}\n`;
    info += `收货地址：${postinfo.address || '无'}\n`;
  } else {
    info += '暂无收货地址信息\n';
  }
  info += '\n';
  return info;
};

// 生成价格信息
const generatePriceInfo = (orderData: any): string => {
  let info = '【价格信息】\n';
  info += `打印费用：${orderData.printPrice || '0'}元\n`;
  info += `邮寄费用：${orderData.postPrice || '0'}元\n`;
  info += `订单总价：${orderData.totalPrice || '0'}元\n`;
  if (orderData.discountPrice) {
    info += `优惠金额：${orderData.discountPrice}元\n`;
  }
  if (orderData.realPrice) {
    info += `实际支付：${orderData.realPrice}元\n`;
  }
  info += '\n';
  return info;
};

// 生成其他信息
const generateOtherInfo = (orderData: any): string => {
  let info = '';

  // 物流信息
  if (orderData.realNo || orderData.postcode) {
    info += '【物流信息】\n';
    if (orderData.realNo) {
      info += `真实订单号：${orderData.realNo}\n`;
    }
    if (orderData.postcode) {
      info += `快递单号：${orderData.postcode}\n`;
    }
    info += '\n';
  }

  // 售后信息
  if (orderData.reason || orderData.backPrice || orderData.processResult) {
    info += '【售后信息】\n';
    if (orderData.reason) {
      info += `售后原因：${orderData.reason}\n`;
    }
    if (orderData.backPrice) {
      info += `退款金额：${orderData.backPrice}元\n`;
    }
    if (orderData.processResult) {
      info += `处理结果：${orderData.processResult}\n`;
    }
    info += '\n';
  }

  // 优惠券信息
  if (orderData.coupon) {
    info += '【优惠券信息】\n';
    const { coupon } = orderData;
    info += `优惠券名称：${coupon.name || '无'}\n`;
    info += `优惠券面额：${coupon.price || '0'}元\n`;
    info += `使用门槛：满${coupon.minPrice || '0'}元可用\n`;
    info += `有效期至：${coupon.expireAt || '无'}\n`;
    info += '\n';
  }

  return info;
};

// 生成文件统计信息
const generateFileStats = (orderData: any): string => {
  let info = '【打印文件统计】\n';
  info += `文件总数：${orderData.prints?.length || 0}个\n`;
  if (orderData.prints && orderData.prints.length > 0) {
    const totalPages = orderData.prints.reduce((sum: number, print: any) => sum + (print.pageCount || 0), 0);
    const totalSize = orderData.prints.reduce((sum: number, print: any) => sum + (print.fileSize || 0), 0);
    info += `总页数：${totalPages}页\n`;
    info += `文件总大小：${(totalSize / 1024 / 1024).toFixed(2)}MB\n`;
  }
  info += '\n';
  return info;
};

// 生成订单信息文档
export const generateOrderInfoDoc = (orderData: any): string => {
  let doc = '订单详细信息\n';
  doc += `${'='.repeat(50)}\n\n`;

  doc += generateBasicInfo(orderData);
  doc += generatePostInfo(orderData);
  doc += generatePriceInfo(orderData);
  doc += generateOtherInfo(orderData);
  doc += generateFileStats(orderData);

  doc += `${'='.repeat(50)}\n`;
  doc += `文档生成时间：${new Date().toLocaleString()}\n`;
  doc += '订单信息文档生成完成';

  return doc;
};

// 生成打印配置说明文档
export const generatePrintConfigDoc = (prints: any[]): string => {
  let doc = '订单打印配置说明\n';
  doc += `${'='.repeat(50)}\n\n`;
  doc += `生成时间：${new Date().toLocaleString()}\n`;
  doc += `文件总数：${prints.length} 个\n\n`;

  const fileNameCounts: { [key: string]: number } = {};

  prints.forEach((print, index) => {
    // 处理同名文件计数
    const originalName = print.name;
    if (fileNameCounts[originalName]) {
      fileNameCounts[originalName]++;
    } else {
      fileNameCounts[originalName] = 1;
    }

    const currentCount = fileNameCounts[originalName];
    const displayName = currentCount > 1
      ? `${originalName}(${currentCount - 1})` : originalName;

    doc += `${index + 1}. 文件名：${displayName}\n`;

    if (print.meta) {
      const { meta } = print;
      const configs: string[] = [];

      if (meta.name) {
        configs.push(`纸张规格：${meta.name}`);
      }
      if (meta.color) {
        configs.push(`颜色：${meta.color}`);
      }
      if (meta.type) {
        configs.push(`单双面：${meta.type}`);
      }
      if (meta.zdType) {
        configs.push(`装订：${meta.zdType}`);
      }
      if (meta.fqType) {
        configs.push(`封面：${meta.fqType}`);
      }
      if (meta.range && meta.range.length) {
        configs.push(`页码范围：${meta.range.join('-')}页`);
      }
      if (meta.count) {
        configs.push(`份数：${meta.count}份`);
      }

      if (configs.length > 0) {
        doc += `   打印配置：${configs.join(' | ')}\n`;
      } else {
        doc += '   打印配置：无特殊配置\n';
      }
    } else {
      doc += '   打印配置：无配置信息\n';
    }

    if (print.price) {
      doc += `   打印费用：${print.price}元\n`;
    }

    doc += `   文件格式：${print.ext?.toUpperCase() || '未知'}\n`;
    doc += `   文件大小：${print.fileSize ? `${(print.fileSize / 1024).toFixed(2)}KB` : '未知'}\n`;
    doc += `   页数：${print.pageCount || '未知'}页\n`;
    doc += '\n';
  });

  doc += `${'='.repeat(50)}\n`;
  doc += '说明文档生成完成';

  return doc;
};
